import { FC, useEffect, useState } from 'react';
import {
  Table,
  TableHeading,
  TableCell,
  Text,
  Button,
  IconButton,
  Select,
  StatusIconProtoProps,
  Snackbar,
  Snack,
  Skeleton,
} from '@nike/eds';
import { useI18nContext } from '../../context/language/use-i18n-context';
import { useSponsorContext } from '../../context/Sponsor';
import ts from './i18n';
import styles from './family.module.css';
import { VisitorPass } from '../../models/IVisitorPass';
import {
  GetFamilyPass,
  GetFamilyRelationships,
  ResendEmailByPassId,
  InactivatePass,
} from '../../services/pass-service';
import Banner from 'components/shared/banner/banner';
import {
  getActiveFamilyCount,
  getGridForFamily,
  FamilyGridModel,
  FamilyGridHelperProps,
} from './familyHelper';
import { PASSSTATUSES } from './options';
import { usePassPolicies } from 'context/PassPolicy';
import { PassPolicy } from 'models/IPassPolicy';
import FamilyForm from './family-form';
import { FamilyRelationship } from '../../models/IRelationShips';
import { FamilyPass } from '../../models/IFamilyPass';
import FamilyResendEmail from './resend-email/resend-email';
import RemoveFamily from './remove/remove';
import { snackStatus } from './type';
import { AppConfig } from 'utils/constants';

export type Option = { value: number; label: string };
export const Family: FC = () => {
  const { i18nString } = useI18nContext();
  const sponsorContext = useSponsorContext();
  const passPoliciesContext = usePassPolicies();
  const sponsorDetails = sponsorContext?.sponsor;
  const sponsorCountry = sponsorDetails?.country;
  const isChinaRegion = sponsorCountry === 'China';

  const [resendMailModalData, setResendMailModalData] = useState<{
    showModal: boolean;
    email: string;
    visitorId: number;
    isSwooshRegistered?: boolean;
  }>();
  const [familyPasses, setFamilyPasses] = useState<VisitorPass[]>();
  const [error, setError] = useState<string>();
  const [gridData, setGridData] = useState<FamilyGridModel[]>([]);
  const [editFamilyPass, setEditFamilyPass] = useState<FamilyPass | null>(null);
  const [familyCount, setFamilyCount] = useState<number>(0);
  const [isModalOpen, setisModalOpen] = useState<boolean>(false);
  const [sorting, setSorting] = useState<{ key: string; asc: boolean }>({
    key: 'column1',
    asc: true,
  });
  const [familyPolicies, setFamilyPolicies] = useState<PassPolicy[]>([]);
  const [familyRelationships, setFamilyRelationships] = useState<FamilyRelationship[]>([]);
  const [removeModal, setRemoveModal] = useState<{
    show: boolean;
    visitorId: number;
    name: string;
  }>({ show: false, visitorId: 0, name: '' });

  const [loading, setLoading] = useState<boolean>(true);

  const [filterStatus, setFilterStatus] = useState<Option>({
    label: i18nString(ts.DDL_ACTIVE),
    value: 0,
  });
  const [snackBar, setSnackBar] = useState({
    showSnack: false,
    message: '',
    snackStatus: 'success' as StatusIconProtoProps['status'],
  });

  const passStatusChange = (option: Option) => {
    setFilterStatus(option);

    const familyProps: FamilyGridHelperProps = {
      familyPolicies: familyPolicies ?? [],
      option: option.value,
      familyPasses: familyPasses ?? [],
      i18nString: i18nString,
    };

    setGridData(getGridForFamily(familyProps));
    setSorting({ key: 'name', asc: true });
  };

  const applySorting = (key: string) => {
    if (sorting.key === key && key !== 'action' && key !== 'icons') {
      setSorting({ ...sorting, asc: !sorting.asc });
    } else {
      setSorting({ key: key, asc: true });
    }
  };
  useEffect(() => {
    const sortedData = [...gridData].sort((a, b) => {
      const valueA = a[sorting.key]?.toString().toLowerCase() || '';
      const valueB = b[sorting.key]?.toString().toLowerCase() || '';

      if (valueA < valueB) return sorting.asc ? -1 : 1;
      if (valueA > valueB) return sorting.asc ? 1 : -1;
      return 0;
    });
    setGridData(sortedData);
  }, [sorting]);
  useEffect(() => {
    if (sponsorDetails?.sponsorId && passPoliciesContext?.passPolicies && isModalOpen === false) {
      const filteredPolicies = passPoliciesContext?.passPolicies.filter(x =>
        ['PASS_TYPE_FAMILY', 'PASS_TYPE_CONVERSE_FAMILY', 'PASS_TYPE_SWOOSH_FAMILY'].includes(
          x.passTypeToken,
        ),
      );
      setFamilyPolicies(filteredPolicies ?? []);
      GetFamilyRelationships(sponsorDetails?.sponsorId).then(response => {
        setFamilyRelationships(response.data);
      });
      GetFamilyPass(sponsorDetails?.sponsorId)
        .then(response => {
          setFamilyPasses(response.data);
          setFamilyCount(getActiveFamilyCount(response.data));

          const familyProps: FamilyGridHelperProps = {
            familyPolicies: filteredPolicies ?? [],
            option: 0, // default to active
            familyPasses: response.data ?? [],
            i18nString: i18nString,
          };
          setGridData(getGridForFamily(familyProps));
          applySorting('name');
          setLoading(false);
        })
        .catch(error => {
          setError(error);
          setLoading(false);
        });
    }
  }, [sponsorDetails?.sponsorId, passPoliciesContext?.passPolicies, isModalOpen]);

  const columns = [
    { headerName: ts.GRID_HEADER_NAME.stringKey, field: 'name' },
    { headerName: ts.ACCESS_TEXT.stringKey, field: 'icons' },
    {
      headerName: ts.GRID_HEADER_RELATIONSHIP.stringKey,
      field: 'relationship',
    },
    { headerName: ts.GRID_HEADER_STATUS.stringKey, field: 'status' },
    { headerName: ts.GRID_HEADER_ACTIONS.stringKey, field: 'action' },
  ];
  const handleEditFamilyPass = (pass: FamilyGridModel) => {
    setEditFamilyPass({
      visitorId: pass.visitorId,
      firstName: pass.firstName,
      lastName: pass.lastName,
      relationshipId: pass.relationshipId,
      access: pass.access,
      passPlayIdRegistered: pass.passPlayIdRegistered,
    });
    setisModalOpen(true);
  };

  const isConverseFamilyPolicy = familyPolicies.some(
    policy => policy.passTypeToken === 'PASS_TYPE_CONVERSE_FAMILY',
  );

  const handleResendEmail = (visitorId: number, email: string, isSwooshRegistered: boolean) => {
    setResendMailModalData({
      showModal: true,
      email: email,
      visitorId: visitorId,
      isSwooshRegistered: isSwooshRegistered,
    });
  };

  const handleResend = async (visitorId: number) => {
    //get swoosh passId
    const swooshPass = familyPasses?.find(
      pass => pass.visitorId === visitorId && pass.passTypeToken === 'PASS_TYPE_SWOOSH_FAMILY',
    );
    if (swooshPass?.passId)
      try {
        await ResendEmailByPassId(swooshPass?.passId);
        setSnackBar({
          showSnack: true,
          snackStatus: 'success',
          message: 'Email successfully resent',
        });
      } catch {
        setSnackBar({
          showSnack: true,
          snackStatus: 'error',
          message: 'Error resending email',
        });
      }
    setResendMailModalData({ showModal: false, email: '', visitorId: 0 });
  };

  const handleRemoveFamilyModal = (visitorId: number, name: string) => {
    setRemoveModal({ show: true, visitorId, name });
  };

  const refreshFamilyData = async (sponsorId: number | undefined) => {
    if (!sponsorId) return;
    const response = await GetFamilyPass(sponsorId);
    setFamilyPasses(response.data);
    setFamilyCount(getActiveFamilyCount(response.data));

    const familyProps: FamilyGridHelperProps = {
      familyPolicies: familyPolicies ?? [],
      option: filterStatus.value,
      familyPasses: response.data ?? [],
      i18nString: i18nString,
    };
    setGridData(getGridForFamily(familyProps));
    setSorting({ key: 'name', asc: true });
    return response.data;
  };

  const handleRemoveFamily = async () => {
    if (!familyPasses || !sponsorDetails?.sponsorId) return;
    const passesToRemove = familyPasses.filter(pass => pass.visitorId === removeModal.visitorId);
    try {
      await Promise.all(passesToRemove.map(pass => InactivatePass(pass.passId)));
      // Refresh family passes after successful inactivation
      try {
        setLoading(true);
        await refreshFamilyData(sponsorDetails.sponsorId);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
      setSnackBar({
        showSnack: true,
        snackStatus: 'success',
        message: 'Removed family member',
      });
    } catch {
      setSnackBar({
        showSnack: true,
        snackStatus: 'error',
        message: 'Failed to remove',
      });
    }
    setRemoveModal({ show: false, visitorId: 0, name: '' });
  };

  return (
    <>
      {!loading ? (
        <section
          className={`eds-flex eds-flex--flex-direction-row  section-cont ${styles.family_section}`}
        >
          <div className="eds-flex eds-flex--justify-content-center div-flex pass_avl">
            <Text as={'h1'} className="h1-100">
              {familyCount}
            </Text>
            <Text as={'h3'} color="white">
              {i18nString(ts.INFOBOX_FAMILY_MEMBERS)}
            </Text>
            <Button
              size="small"
              variant="primary"
              data-testid="manage-myaccess"
              className="btn_green eds-spacing--my-20 txt-black"
              onClick={() => setisModalOpen(true)}
            >
              {i18nString(ts.FAMILY_MODAL_ADD_FAMILY)}
            </Button>
          </div>
          <Banner width="65%" />
        </section>
      ) : (
        <section
          className={`eds-flex eds-flex--flex-direction-row  section-cont ${styles.family_section}`}
        >
          <div className="eds-flex eds-flex--justify-content-center div-flex pass_avl">
            <div style={{ width: '100%', margin: '10px', padding: '50px', justifyItems: 'center' }}>
              <Skeleton height={150} width={150} className="eds-spacing--mr-16" />
              <br />
              <Text as={'h3'} color="white">
                {i18nString(ts.INFOBOX_FAMILY_MEMBERS)}
              </Text>
              <Skeleton
                height={30}
                width={'30%'}
                className="btn_green eds-spacing--my-20 txt-black"
                data-testid="family-skelton"
              />
            </div>
          </div>
          <Banner width="65%" />
        </section>
      )}

      <section className="eds-spacing--px-48">
        <div className="eds-flex eds-flex--align-items-center eds-flex--justify-content-space-between eds-flex--wrap eds-spacing--mb-12 eds-spacing--mt-28">
          <h2 className={`h2_font eds-text--align-center txt-align-center flex-33`}>
            {i18nString(ts.FAMILY_ACTIVE_PAGE_NAME)}
          </h2>
          <Select
            id="passstatus"
            options={PASSSTATUSES?.map(passType => ({
              value: passType.value,
              label: i18nString(ts[passType.label as keyof typeof ts]),
            }))}
            label="menu Pass Type"
            data-testid="menu-pass-type"
            value={filterStatus}
            onChange={option => {
              passStatusChange(option as Option);
            }}
            name="passstatus"
            className={`eds__select eds-flex eds-flex--align-items-flex-center eds-flex--justify-content-flex-end flex-33 eds-spacing--mr-28`}
            hideLabel={true}
            aria-label="menu Pass Type"
            aria-hidden={false}
            isSearchable={false}
          />
        </div>

        {!loading ? (
          <>
            <div className="eds-flex eds-flex--justify-content-center eds-flex--align-items-flex-center">
              <Table className={`eds-table`} width="100%">
                <thead>
                  <tr key="accessAccountHeader">
                    {columns.map(column => {
                      if (
                        column.headerName === ts.ACCESS_TEXT.stringKey &&
                        !isConverseFamilyPolicy
                      ) {
                        return null; // Skip rendering this column if Converse Family policy is not present
                      }
                      return (
                        <TableHeading
                          className={styles.table_header}
                          data-testid={column.headerName}
                          key={column.headerName}
                        >
                          <span className={`eds-flex eds-flex--align-items-center`}>
                            <span className={`table_header_style`}>
                              {i18nString(ts[column.headerName as keyof typeof ts])}
                            </span>
                            {column.field !== 'action' && column.field !== 'icons' && (
                              <IconButton
                                icon={
                                  sorting.key === column.field
                                    ? sorting.asc
                                      ? 'SortAscending'
                                      : 'SortDescending'
                                    : 'Sort'
                                }
                                label="SortIcon"
                                variant="ghost"
                                size="small"
                                aria-label={`Sort by ${i18nString(ts[column.headerName as keyof typeof ts])}, ${
                                  sorting.key === column.field
                                    ? sorting.asc
                                      ? 'currently sorted ascending'
                                      : 'currently sorted descending'
                                    : 'not sorted'
                                }`}
                                onClick={() => applySorting(column.field)}
                                onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    applySorting(column.field);
                                  }
                                }}
                                id={`sort-button-${column.field}`}
                              ></IconButton>
                            )}
                          </span>
                        </TableHeading>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {gridData.map((visitor, index) => (
                    <tr key={index}>
                      <TableCell className={styles.table_td + ' ' + styles.grid_cell_name}>
                        {visitor.name}
                      </TableCell>
                      {isConverseFamilyPolicy && (
                        <TableCell className={styles.table_td}>
                          {visitor.access.map((item, index) => (
                            <img
                              key={index}
                              title={item.title}
                              src={item.src}
                              alt="icon"
                              className={styles.grid_image}
                            />
                          ))}
                        </TableCell>
                      )}
                      <TableCell className={styles.table_td}>{visitor.relationship}</TableCell>
                      <TableCell className={styles.table_td}>{visitor.status}</TableCell>
                      <TableCell key={index}>
                        <IconButton
                          label="edit pass"
                          aria-label="edit pass"
                          size="small"
                          disabled={false}
                          onClick={() => handleEditFamilyPass(visitor)}
                          icon={'Edit'}
                          variant="ghost"
                        ></IconButton>
                        <IconButton
                          label="resend email"
                          aria-label="resend email"
                          size="small"
                          onClick={() =>
                            handleResendEmail(
                              visitor.visitorId,
                              visitor.swooshEmail ?? '',
                              visitor.swooshStatus == AppConfig.passStatus.registered,
                            )
                          }
                          disabled={
                            !(
                              visitor.swooshStatus != undefined &&
                              visitor.swooshStatus === AppConfig.passStatus.registered
                            )
                          }
                          icon={'InboxFilled'}
                          variant="ghost"
                          data-testid="resend-email"
                        ></IconButton>
                        <IconButton
                          label="remove family"
                          aria-label="remove family"
                          size="small"
                          disabled={
                            visitor?.passes.filter(
                              pass => pass.passStatusId !== AppConfig.passStatus.inactive,
                            ).length > 0
                              ? false
                              : true
                          }
                          onClick={() => handleRemoveFamilyModal(visitor.visitorId, visitor.name)}
                          icon={'Close'}
                          variant="ghost"
                          data-testid="remove family"
                        ></IconButton>
                      </TableCell>
                    </tr>
                  ))}

                  {(gridData.length === 0 || error !== undefined) && (
                    <tr>
                      <TableCell data-testid="emptyText" className={styles.table_td} colSpan={4}>
                        {error ? error : i18nString(ts.FAMILY_EMPTY_TEXT).replace(/\\/g, '"')}
                      </TableCell>
                    </tr>
                  )}
                </tbody>
              </Table>

              {isModalOpen && (
                <FamilyForm
                  isModalOpen={isModalOpen}
                  isChinaRegion={isChinaRegion}
                  familyRelationships={familyRelationships}
                  activeFamilyCount={familyCount}
                  onDismiss={() => {
                    setisModalOpen(false);
                    setEditFamilyPass(null);
                  }}
                  setSnackbar={setSnackBar}
                  familyPassToEdit={editFamilyPass}
                />
              )}
              <Snackbar>
                {snackBar.showSnack && (
                  <Snack
                    id="family-snackbar"
                    hideDismiss={true}
                    className="txt_uppercase"
                    autoDismissDuration={5000}
                    status={snackBar.snackStatus as snackStatus}
                    onDismiss={() => setSnackBar(prev => ({ ...prev, show: false }))}
                  >
                    {snackBar.message}
                  </Snack>
                )}
              </Snackbar>
            </div>
            <FamilyResendEmail
              isActiveSwoosh={resendMailModalData?.isSwooshRegistered ?? false}
              visitorId={resendMailModalData?.visitorId ?? 0}
              onDismiss={() =>
                setResendMailModalData({ showModal: false, email: '', visitorId: 0 })
              }
              onResend={handleResend}
              email={resendMailModalData?.email}
              showModal={resendMailModalData?.showModal}
            />
            <RemoveFamily
              isOpen={removeModal.show}
              familyMemberName={removeModal.name}
              onKeep={() => setRemoveModal({ show: false, visitorId: 0, name: '' })}
              onRemove={handleRemoveFamily}
            />
          </>
        ) : (
          <>
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton
                data-testid={`pass-skeleton ${index}`}
                key={index}
                height={50}
                width={`${100}%`}
                style={{ margin: 'var(--eds-space-16)' }}
              />
            ))}
          </>
        )}
      </section>

      <Snackbar>
        {snackBar.showSnack && (
          <Snack
            id="familysnackbar"
            hideDismiss={true}
            autoDismissDuration={5000}
            status={snackBar.snackStatus as snackStatus}
            onDismiss={() => setSnackBar(prev => ({ ...prev, showSnack: false }))}
          >
            {snackBar.message}
          </Snack>
        )}
      </Snackbar>
    </>
  );
};
